import { useCommonClient } from '@/app/common/commonClientProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { URLParameters } from '@/app/common/urlParameters';
import { PAGE_TYPE } from '@/app/common/pageType';
import type { ChangeFlightPage } from '@/app/common/starTrack/events/utils/starTrack.types';
import { createFlightDetailsClickedPayload } from '@/app/common/starTrack/events/flightDetailsClicked.ts';
import { createFlightChangedPayload } from '@/app/common/starTrack/events/flightChanged.ts';
import { useChangeTransportDetailsModal } from '@/app/components/hotel-detail/changeTransport/ChangeTransportDetailsModalProvider';
import { getOutboundInboundIdParams } from '@/app/components/hotel-detail/common/tracking';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import useTransportsFetch from '@/app/components/hotel-detail/travel-api/transportsFetch';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { format, parse } from 'date-fns';
import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getActionId } from '@/app/components/hotel-detail/lib/tracking';
import { trackingProductSelected } from '@/app/common/tracking/trackingProductSelected.ts';

export const useChangeTransportHandler = (
  onChangeTransport?: (transportId: string, transportClass: string) => void,
  onFlightSelected?: (transportId: string, brandedFareId?: string) => void
) => {
  const { selectedTransport } = useChangeTransportDetailsModal();
  const { selectedMealPlan, selectedRoom } = useSelectedMealPlan();
  const { data: currentTransportInSearch } = useSelectedTransport();
  const { data: alternativeTransports } = useTransportsFetch();
  const { starTrack, handleTransportChosen, handleFlightClicked } = useCommonClient();
  const { sendCuriosityFlightRedirectEvent } = useCuriosity();
  const { productDetailKey, flightClickId, defaultTransportId } = useInitialParams();
  const { searchInfoData, commonData, localization } = usePageWrapperData();
  const { offersData } = useOffersContext();
  const { isTrain, children: numberOfChildren, infants, nights: nightsFromUrl } = useUrlSearchParams();
  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];

  const isMobile = commonData.isMobileDevice;
  const initialParams = useInitialParams();

  const handleContinueClick = useCallback(
    ({
      transportId,
      transportClass,
      brandedFareId,
    }: {
      transportId: string;
      transportClass: string;
      brandedFareId?: string;
    }) => {
      const currentTransport = currentTransportInSearch?.transports[0];
      const defaultTransport =
        !defaultTransportId || currentTransport?.id === defaultTransportId
          ? currentTransport
          : (alternativeTransports as FullAvailableTransportResponse)?.availableTransports?.find(
              (currentAvailTransport: { id: string }) => currentAvailTransport.id === defaultTransportId
            );
      const transportHasChanged = transportId !== currentTransport?.id;
      const isDefaultFlight = transportId === defaultTransportId;
      const evolutionEnabled = localization.configValuesApp?.evolutionEnabled;
      const extendedSelectedTransport = (
        alternativeTransports as FullAvailableTransportResponse
      )?.availableTransports?.find((currentAvailTransport: { id: string }) => currentAvailTransport.id === transportId);

      if (onChangeTransport && transportHasChanged) {
        // MPH-646: in Evo an applied flight change starts a new flight-viewing session — rotate
        // the flightClickId now, before any of the new flight's data lands, so everything that
        // fires or (re)mounts with it (BRANDED_FARES_*, the book-click funnel events,
        // CLICK2CHECKOUT) joins on the new id. See rotateEvoFlightClickId in
        // pageWrapperClientDetails for the page-entry rotations.
        if (evolutionEnabled) {
          handleFlightClicked(uuidv4().slice(0, 32), extendedSelectedTransport?.eligibleBrandedFares ?? false);
        }
        onChangeTransport(transportId, transportClass);
      }

      // MPH-558/MPH-646: in Evo, confirming a flight in the change-flight popup is not a funnel
      // step — FLIGHT_DETAILS_CLICKED and DETAIL_BOOK_CLICKED are emitted together by the
      // flight-page book click instead (useTrackEvoBookClicked).
      if (!evolutionEnabled) {
        starTrack.flightDetailsClicked(
          createFlightDetailsClickedPayload({
            searchId: searchId || '',
            productDetailKey,
            flightClickId: flightClickId || '',
          })
        );
      }

      if (!isMobile && selectedMealPlan && !evolutionEnabled) {
        trackingProductSelected({
          starTrack,
          searchParams,
          searchInfoData,
          commonData,
          localization,
          initialParams,
          offersData,
          actionId: getActionId(initialParams.actionIdSeed, searchId),
          selectedMealPlan,
          roomId: selectedRoom?.roomId || '',
        });
      }

      if (transportHasChanged) {
        // Origin of the change-flight redirection, read live since the change-transport modal persists
        // across the hotel-detail → flight navigation: hotel detail page vs flight page.
        const changeFlightOriginPage: ChangeFlightPage =
          getSearchParams()[URLParameters.PAGE_TYPE] === PAGE_TYPE.HOTEL_DETAIL ? 'Hotel Detail' : 'Flight Detail';
        starTrack.flightChanged(
          createFlightChangedPayload({
            searchId: searchId || '',
            productDetailKey,
            page: changeFlightOriginPage,
            currentTransport,
            transportId,
            extendedSelectedTransport,
          })
        );
      }
      const { outboundFlightId, inboundFlightId } = getOutboundInboundIdParams(
        extendedSelectedTransport?.outbound,
        extendedSelectedTransport?.inbound
      );

      sendCuriosityFlightRedirectEvent({
        people_child: numberOfChildren,
        people_infant: infants,
        checkin_date: offersData?.checkin
          ? format(parse(offersData.checkin, 'yyyyMMdd', new Date()), 'yyyy-MM-dd')
          : '',
        checkout_date: offersData?.checkout
          ? format(parse(offersData.checkout, 'yyyyMMdd', new Date()), 'yyyy-MM-dd')
          : '',
        nights: offersData?.nights || nightsFromUrl,
        product_type: isTrain ? 'TH' : 'DP',
        total_price: selectedMealPlan?.finalTotalPrice,
        total_price_currency: localization.currency,
        default_outbound_flight_id: outboundFlightId,
        default_inbound_flight_id: inboundFlightId,
        departure_flight_small_bag_included: selectedTransport?.inbound?.handBaggageIncluded ? 1 : 0,
        return_flight_small_bag_included: selectedTransport?.outbound?.handBaggageIncluded ? 1 : 0,
        outbound_departure_time: selectedTransport?.outbound?.departureDate
          ? format(
              parse(selectedTransport?.outbound?.departureDate, 'dd/MM/yy#HH:mm', new Date()),
              'yyyy-MM-dd HH:mm:ss'
            )
          : '',
        inbound_departure_time: selectedTransport?.inbound?.departureDate
          ? format(
              parse(selectedTransport?.inbound?.departureDate, 'dd/MM/yy#HH:mm', new Date()),
              'yyyy-MM-dd HH:mm:ss'
            )
          : '',
        default_flight_is_direct:
          defaultTransport?.outbound.segments.length === 1 && defaultTransport?.inbound.segments.length === 1 ? 1 : 0,
        is_default_flight: isDefaultFlight ? 1 : 0,
      });

      if (onFlightSelected) {
        onFlightSelected(transportId, brandedFareId);
      } else {
        handleTransportChosen(transportId, brandedFareId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedTransport,
      selectedMealPlan,
      alternativeTransports,
      currentTransportInSearch,
      offersData,
      productDetailKey,
      flightClickId,
      defaultTransportId,
      searchId,
      onChangeTransport,
      onFlightSelected,
      sendCuriosityFlightRedirectEvent,
      handleTransportChosen,
      handleFlightClicked,
    ]
  );

  return { handleContinueClick };
};
